import {useEffect, useState} from 'react';

import {useQuery} from '@apollo/react-hooks';
import {SEARCH_SONGS_QUERY} from './graphql/SearchSongQuery';
import {searchSongs, searchSongsVariables} from './graphql/types/searchSongs';
import {Song} from '../types';

export default function useSearch(
  searchTerm: string | null,
  take: number,
): [Song[] | null, boolean] {
  const [searchResults, setSearchResults] = useState<Song[] | null>(null);

  const debounceSearch = useDebounce(searchTerm, 100);

  const {data: songSearchResults} = useQuery<searchSongs, searchSongsVariables>(
    SEARCH_SONGS_QUERY,
    {
      variables: {search_term: debounceSearch, limit: take},
    },
  );

  useEffect(() => {
    if (songSearchResults) {
      setSearchResults(songSearchResults.music.searchSongs);
    } else if (searchTerm && searchTerm.trim() === '') {
      setSearchResults(null);
    }
  }, [searchTerm, songSearchResults]);

  const loading = searchResults == null;

  return [searchResults, loading];
}

// Hook
function useDebounce(value: any, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}
